import {
  rankWith,
  and,
  isControl,
  schemaMatches,
  update,
  ControlProps,
} from '@jsonforms/core';
import { withJsonFormsControlProps, useJsonForms } from '@jsonforms/react';
import {
  DimensionValuesEditor,
  InheritSource,
} from '../DimensionValuesEditor';

/**
 * DimensionSetEditor — the JSONForms binding of the inline dimension-values editor
 * (Master-Data-Dimensions spec, Part 2). The editing UI itself lives in
 * <see>DimensionValuesEditor</see> (shared with hand-written panels such as the org tree's
 * Dimensions tab); this wrapper only adapts it to JSONForms.
 *
 * Inmatning vs Lagring: the user enters a value per active dimension; the system stores a single
 * `DefaultDimensionSetId` FK to the immutable, deduplicated DimensionSet. This renderer NEVER
 * shows a DimensionSet picker — the resolved set id is produced by resolve-on-save (see
 * detail-view handleSave), not here.
 *
 * Form-state contract with the save step: this renderer stashes the working {defId → valueId}
 * map at the sibling key `<field>__values`; the generated handleSave POSTs it to
 * /api/finance/dimension-set/resolve and injects the returned id into the entity payload.
 *
 * Inherited defaults: when the schema's `x-dimension-set.inheritFrom` names a form-data key
 * (e.g. "parentId" on OrgUnit or ProductGroup), the editor shows the effective defaults of that
 * node's ancestor chain — grayed, with the contributing node — for axes this record leaves
 * unset. `x-dimension-set.inheritEntity` (the owning entity's name, emitted by the schema
 * generator) picks which tree the backend walks; absent means OrgUnit (pre-inheritEntity schemas).
 */

/** inheritEntity (DSL entity name) → the tree the effective-defaults endpoint walks. */
const INHERIT_SOURCE_BY_ENTITY: Record<string, InheritSource> = {
  OrgUnit: 'org-unit',
  ProductGroup: 'product-group',
};

/** Tester: matches a control whose schema says it is a dimension-set field. */
export const dimensionSetEditorTester = rankWith(
  10,
  and(isControl, schemaMatches((schema) => schema.format === 'dimension-set')),
);

export const DimensionSetEditorComponent = (props: ControlProps) => {
  const { data, path, label, description, schema, visible, enabled } = props;
  const ctx = useJsonForms();

  const dimensionSetConfig = (schema as any)?.['x-dimension-set'] || {};
  const module = dimensionSetConfig.module || '';
  // Inheritance opt-in: the schema names the form-data key holding the tree node whose
  // ancestor chain supplies inherited defaults (e.g. parentId on OrgUnit / ProductGroup).
  const inheritFromKey: string | undefined = dimensionSetConfig.inheritFrom;
  const inheritFromNodeId = inheritFromKey
    ? ((ctx.core?.data as any)?.[inheritFromKey] as string | undefined)
    : undefined;
  // The owning DSL entity — emitted on every dimension-set field. Picks the inheritance tree AND
  // lets the editor find this record's rung on the plan-derivation precedence ladder.
  const entity: string | undefined = dimensionSetConfig.entity;
  const inheritSource = INHERIT_SOURCE_BY_ENTITY[entity ?? 'OrgUnit'];

  if (visible === false) return null;

  return (
    <DimensionValuesEditor
      module={module}
      dimensionSetId={data}
      inheritFromNodeId={inheritSource ? inheritFromNodeId : undefined}
      inheritSource={inheritSource}
      entity={entity}
      disabled={enabled === false}
      label={label || 'Dimensions'}
      description={description}
      onChange={(map) => ctx.dispatch?.(update(`${path}__values`, () => map))}
    />
  );
};

export const DimensionSetEditor = withJsonFormsControlProps(DimensionSetEditorComponent);

export const dimensionSetEditorEntry = {
  tester: dimensionSetEditorTester,
  renderer: DimensionSetEditor,
};
